import React, { useEffect, useRef, useState } from "react";
import useTranslate from "../../core/languages/hooks/useTranslate";
import { useFormContext } from "react-hook-form";
import RadioButtonGroup from "../../ui/components/RadioButtonGroup/RadioButtonGroup";
import ClearDataButton from "../../common/components/filters/ClearDataButton";

export enum TransferDirectionEnum {
    ARRIVAL = 0,
    DEPARTURE = 1,
    ROUND_TRIP = 2,
}

const statusChoicesAll = [
    { id: TransferDirectionEnum.ARRIVAL, name: 'common.enum.transferDirection.oneWay' },
    { id: TransferDirectionEnum.DEPARTURE, name: 'common.enum.transferDirection.comeBack' },
    { id: TransferDirectionEnum.ROUND_TRIP, name: 'common.enum.transferDirection.both' },
];

// RadioButton Input - Para formularios
export const TransferDirectionRadioInput = ({
    allowRoundTrip = true,
    name,
    value,
    onChange,
    disabled = false,
}: {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    allowRoundTrip?: boolean;
    name?: string;
    value?: any;
}) => {
    const t = useTranslate();
    const { formState: { errors } } = useFormContext();

    const choices = allowRoundTrip
        ? statusChoicesAll
        : statusChoicesAll.filter(choice => choice.id !== TransferDirectionEnum.ROUND_TRIP);

    // Convert choices to the format expected by RadioButtonGroup
    const radioOptions = choices.map(choice => ({
        id: choice.id,
        label: t(choice.name),
    }));

    return (
        <RadioButtonGroup
            options={radioOptions}
            value={value}
            onChange={onChange}
            name={name || "direction"}
            disabled={disabled}
            error={(errors.direction as any)?.message}
        />
    );
};

// Select Filter - Para filtros sin formulario
export interface TransferDirectionSelectProps {
    onChange: (updates: any) => void;
    value?: number;
    allowRoundTrip?: boolean;
}

export const TransferDirectionSelect: React.FC<TransferDirectionSelectProps> = ({
    onChange, value, allowRoundTrip = true,
}) => {
    const t = useTranslate();
    const directionRef = useRef<HTMLDivElement | null>(null);
    const [showDirectionPopover, setShowDirectionPopover] = useState(false);
    const [selectedDirection, setSelectedDirection] = useState<number | null>(value || null);

    const choices = allowRoundTrip
        ? statusChoicesAll
        : statusChoicesAll.filter(choice => choice.id !== TransferDirectionEnum.ROUND_TRIP);

    const handleSelectDirection = (directionId: number | null) => {
        setSelectedDirection(directionId);
        setShowDirectionPopover(false);

        if (directionId !== null) {
            onChange({ direction: directionId });
        } else {
            onChange({ direction: undefined });
        }
    };

    const handleClickOutside = (e: MouseEvent) => {
        if (directionRef.current && !directionRef.current.contains(e.target as Node)) {
            setShowDirectionPopover(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const getDirectionLabel = () => {
        if (selectedDirection === null) {
            return t("transfers.fields.direction");
        }
        const choice = choices.find(c => c.id === selectedDirection);
        return choice ? t(choice.name) : t("transfers.fields.direction");
    };

    return (
        <div
            ref={directionRef}
            className="relative w-60 cursor-pointer [ nc-hero-field-padding ] items-center min-w-0"
        >
            <div className="flex items-start">
                <div className="flex-grow min-w-0">
                    <div
                        className="w-full p-0 xl:text-lg font-semibold min-w-0 cursor-pointer text-neutral-800 dark:text-neutral-100"
                        onClick={() => setShowDirectionPopover((v) => !v)}
                    >
                        <span className="whitespace-normal break-words block">
                            {getDirectionLabel()}
                        </span>
                    </div>
                    <span className="block mt-0.5 text-sm text-neutral-400 font-light">
                        {t("transferSearch.message.searchByDirection")}
                    </span>
                </div>

                {selectedDirection !== null && (
                    <ClearDataButton onClick={() => handleSelectDirection(null)} />
                )}
            </div>

            {showDirectionPopover && (
                <div className="absolute left-0 z-40 w-full bg-white dark:bg-neutral-800 top-full mt-3 py-3 rounded-3xl shadow-xl">
                    {choices.map((choice) => (
                        <div
                            key={choice.id}
                            className="flex px-4 sm:px-8 items-center py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                            onClick={() => handleSelectDirection(choice.id)}
                        >
                            <span className="block font-medium text-neutral-700 dark:text-neutral-200">
                                {t(choice.name)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TransferDirectionSelect;

